/**
 * "Werkruimte herindelen": tidy a workspace imported from Obsidian into the
 * Nexus layout. Top: Taken, Projecten, Vergaderingen, Inbox; then the areas
 * (numbers like "02 " dropped); at the bottom Archief, Sjablonen, Dagnotities.
 * Cockpit folders are dissolved (their leftovers go to Archief).
 *
 * The plan is computed from the workspace as it is now and shown first; only
 * applying it changes anything. Nothing is deleted: only pages that are empty
 * (no subpages, no text) go to the trash, everything else is moved or renamed.
 */
import type { Id } from '@nexus/core';
import type { PageRecord, Store } from '@nexus/store';
import { isEmptyDoc, loadPageJSON } from './content';

type Page = PageRecord & { props: Record<string, unknown> };

export type ReorgStep =
  | { kind: 'move'; pageId: Id; title: string; parentId: Id | null; parentTitle: string | null }
  | { kind: 'rename'; pageId: Id; from: string; to: string }
  | { kind: 'trash'; pageId: Id; title: string }
  | { kind: 'setting'; key: string; pageId: Id; title: string };

export interface ReorgPlan {
  steps: ReorgStep[];
  /** The top level afterwards, in order (titles after renaming). */
  root: { id: Id; title: string }[];
}

const NUMBERED = /^\d{1,3}\s*[-.]?\s+/;
const clean = (title: string) => title.replace(NUMBERED, '').trim();
const named = (p: Page, names: string[]) =>
  names.some((n) => clean(p.title).toLowerCase() === n.toLowerCase());

export async function planReorganization(store: Store): Promise<ReorgPlan> {
  const pages = await store.listPagesWithProps();
  const byId = new Map(pages.map((p) => [p.id, p]));
  const childrenOf = (id: Id | null) => pages.filter((p) => p.parentId === id);
  const size = (id: Id): number => childrenOf(id).reduce((n, c) => n + 1 + size(c.id), 0);
  const setting = async (key: string) => {
    const id = await store.getSetting(key);
    return id && byId.has(id) ? byId.get(id)! : null;
  };
  /** The configured page, else the biggest page with one of these names. */
  const special = async (key: string, names: string[]) =>
    (await setting(key)) ??
    pages
      .filter((p) => named(p, names))
      .sort((a, b) => size(b.id) - size(a.id) || a.createdAt - b.createdAt)[0] ??
    null;
  const isEmpty = async (p: Page) =>
    childrenOf(p.id).length === 0 &&
    Object.keys(p.props).length === 0 &&
    isEmptyDoc(await loadPageJSON(store, p.id));

  const tasks = await setting('tasks_parent_id');
  const projects = await setting('projects_db_id');
  const meetings = await setting('meetings_db_id');
  const inbox = await special('inbox_page_id', ['Inbox']);
  const daily = await special('daily_parent_id', ['Dagnotities', 'Dagelijks', 'Daily']);
  const templates = await special('templates_parent_id', ['Sjablonen', 'Templates']);
  const roots = childrenOf(null);
  const archive = roots.find((p) => named(p, ['Archief', 'Archive'])) ?? null;
  const cockpits = roots.filter((p) => named(p, ['Cockpit']));
  const attachments = roots.find((p) => named(p, ['Bijlagen', 'Attachments'])) ?? null;

  const specials = [tasks, projects, meetings, inbox, daily, templates].filter(
    (p): p is Page => !!p,
  );
  const specialIds = new Set(specials.map((p) => p.id));
  const steps: ReorgStep[] = [];
  const trashed = new Set<Id>();
  const toArchive: Page[] = [];

  // Cockpits: special folders come out, empty copies of them go, the rest is archived.
  for (const cockpit of cockpits) {
    for (const child of childrenOf(cockpit.id)) {
      if (specialIds.has(child.id)) continue;
      if (named(child, ['Inbox', 'Dagnotities', 'Dagelijks']) && (await isEmpty(child))) {
        steps.push({ kind: 'trash', pageId: child.id, title: child.title });
        trashed.add(child.id);
      } else toArchive.push(child);
    }
  }
  if (attachments) toArchive.push(attachments);
  if (toArchive.length && archive)
    for (const p of toArchive)
      steps.push({
        kind: 'move',
        pageId: p.id,
        title: p.title,
        parentId: archive.id,
        parentTitle: clean(archive.title),
      });
  // A cockpit that is empty after that goes to the trash; one with text of its own is archived.
  for (const cockpit of cockpits) {
    const left = childrenOf(cockpit.id).filter(
      (c) => !specialIds.has(c.id) && !trashed.has(c.id) && !toArchive.includes(c),
    );
    const ownEmpty =
      Object.keys(cockpit.props).length === 0 && isEmptyDoc(await loadPageJSON(store, cockpit.id));
    if (left.length === 0 && ownEmpty) {
      steps.push({ kind: 'trash', pageId: cockpit.id, title: cockpit.title });
      trashed.add(cockpit.id);
    } else if (archive)
      steps.push({
        kind: 'move',
        pageId: cockpit.id,
        title: cockpit.title,
        parentId: archive.id,
        parentTitle: clean(archive.title),
      });
  }

  // Areas: every other top-level page; numbered ones first, by their number.
  const reserved = new Set<Id>([
    ...specialIds,
    ...cockpits.map((p) => p.id),
    ...(archive ? [archive.id] : []),
    ...(attachments ? [attachments.id] : []),
  ]);
  const number = (p: Page) => Number(/^(\d+)/.exec(p.title)?.[1] ?? Infinity);
  const areas = roots
    .filter((p) => !reserved.has(p.id))
    .map((p, i) => ({ p, i }))
    .sort((a, b) => number(a.p) - number(b.p) || a.i - b.i)
    .map(({ p }) => p);

  // Renames: drop numbers; the app's own names for the special folders.
  const titles = new Map<Id, string>();
  const rename = (p: Page | null, to: string) => {
    if (!p || p.title === to) return;
    steps.push({ kind: 'rename', pageId: p.id, from: p.title, to });
    titles.set(p.id, to);
  };
  for (const a of areas) rename(a, clean(a.title));
  rename(archive, clean(archive?.title ?? ''));
  rename(templates, 'Sjablonen');
  rename(daily, 'Dagnotities');
  rename(inbox, 'Inbox');

  // The top level, in order. Special folders that live deeper move up.
  const order = [tasks, projects, meetings, inbox, ...areas, archive, templates, daily].filter(
    (p): p is Page => !!p,
  );
  for (const p of order)
    if (p.parentId !== null)
      steps.push({ kind: 'move', pageId: p.id, title: p.title, parentId: null, parentTitle: null });

  // The app finds its folders by id from now on.
  const settings: [string, Page | null][] = [
    ['inbox_page_id', inbox],
    ['daily_parent_id', daily],
    ['templates_parent_id', templates],
  ];
  for (const [key, p] of settings)
    if (p && (await store.getSetting(key)) !== p.id)
      steps.push({ kind: 'setting', key, pageId: p.id, title: titles.get(p.id) ?? p.title });

  return {
    steps,
    root: order.map((p) => ({ id: p.id, title: titles.get(p.id) ?? p.title })),
  };
}

/**
 * Apply a plan: moves into folders and renames, then the top level in order
 * (which also lifts special folders out of their parents), the settings, and
 * last the trash, but only for pages that are still empty by then.
 */
export async function applyReorganization(
  store: Store,
  plan: ReorgPlan,
  onProgress: (done: number, total: number) => void = () => {},
): Promise<void> {
  const nested = plan.steps.filter((s) => s.kind !== 'move' || s.parentId !== null);
  const total = nested.length + plan.root.length;
  let done = 0;
  const tick = () => onProgress(++done, total);
  const live = async (id: Id) => {
    const p = await store.getPage(id);
    return !!p && !p.deleted && !p.trashedAt;
  };
  const by = (kind: ReorgStep['kind']) => nested.filter((s) => s.kind === kind);

  for (const s of [...by('move'), ...by('rename')]) {
    if (s.kind === 'move' && s.parentId && (await live(s.pageId))) {
      const siblings = (await store.listPages()).filter((p) => p.parentId === s.parentId);
      await store.movePage(s.pageId, s.parentId, siblings.at(-1)?.id ?? null);
    }
    if (s.kind === 'rename' && (await live(s.pageId)))
      await store.updatePage(s.pageId, { title: s.to });
    tick();
  }
  let after: Id | null = null;
  for (const r of plan.root) {
    if (await live(r.id)) {
      await store.movePage(r.id, null, after);
      after = r.id;
    }
    tick();
  }
  for (const s of by('setting')) {
    if (s.kind === 'setting') await store.setSetting(s.key, s.pageId);
    tick();
  }
  for (const s of by('trash')) {
    const hasChildren = (await store.listPages()).some((p) => p.parentId === s.pageId);
    const empty =
      !hasChildren &&
      Object.keys(await store.getProps(s.pageId)).length === 0 &&
      isEmptyDoc(await loadPageJSON(store, s.pageId));
    if (empty && (await live(s.pageId))) await store.trashPage(s.pageId);
    tick();
  }
}
